import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay, catchError, of, BehaviorSubject } from 'rxjs';

import { environment } from "../../environments/environment";

export interface CarouselItem {
  id: number;
  titulo: string;
  img: string;
  url: string;
  orden: number;
  descripcion?: string;
  promotionId:string;
  promotionName:string;
  createName:string;

}

export interface CarouselResponse {
  tipoDispositivo: {
    xl: CarouselItem[];
    desktop: CarouselItem[];
    tablet: CarouselItem[];
    movil: CarouselItem[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class CarouselService {
  private readonly apiUrl = environment.apiUrl+'/carrusel';
  private cachedResponse: CarouselResponse | null = null;
  private lastFetchTime = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

  // Observable para cambios de dispositivo
  private deviceTypeSubject = new BehaviorSubject<'xl' | 'desktop' | 'tablet' | 'movil'>('desktop');
  public deviceType$ = this.deviceTypeSubject.asObservable();

  constructor(private http: HttpClient) {
    // Inicializar con el tipo de dispositivo actual
    this.deviceTypeSubject.next(this.getCurrentDeviceType());
  }

  getCarouselItems(): Observable<CarouselResponse> {
    const now = Date.now();

    // Retornar cache si está disponible y no ha expirado
    if (this.cachedResponse && (now - this.lastFetchTime) < this.CACHE_DURATION) {
      return of(this.cachedResponse);
    }

    // Hacer nueva llamada y cachear resultado
    return this.http.get<CarouselResponse>(this.apiUrl).pipe(
      map(response => {
        this.cachedResponse = response;
        this.lastFetchTime = now;
        return response;
      }),
      catchError(error => {
        console.error('Error fetching carousel data:', error);
        // Retornar cache anterior si existe, aunque haya expirado
        if (this.cachedResponse) {
          return of(this.cachedResponse);
        }
        throw error;
      }),
      shareReplay(1) // Compartir el resultado con múltiples suscriptores
    );
  }

  getCarouselByDeviceType(deviceType: 'xl' | 'desktop' | 'tablet' | 'movil'): Observable<CarouselItem[]> {
    return this.getCarouselItems().pipe(
      map(response => response.tipoDispositivo[deviceType] || [])
    );
  }

  getCurrentDeviceType(): 'xl' | 'desktop' | 'tablet' | 'movil' {
    const width = window.innerWidth;

    // Breakpoints más precisos
    if (width >= 1200) return 'xl';        // 1280px y superior
    if (width >= 1024) return 'desktop';   // 1024px - 1279px
    if (width >= 768) return 'tablet';     // 768px - 1023px
    return 'movil';                        // Menos de 768px
  }

  // Método para actualizar el tipo de dispositivo (llamado desde el componente)
  updateDeviceType(): void {
    const newDeviceType = this.getCurrentDeviceType();
    const currentDeviceType = this.deviceTypeSubject.value;

    if (newDeviceType !== currentDeviceType) {
      console.log(`[CarouselService] Dispositivo cambiado: ${currentDeviceType} → ${newDeviceType}`);
      this.deviceTypeSubject.next(newDeviceType);
    }
  }

  // Método para limpiar cache (útil para testing o actualizaciones forzadas)
  clearCache(): void {
    this.cachedResponse = null;
    this.lastFetchTime = 0;
  }
}
